/* ====================================================================
 *  gridItems — розгортання товарів у плоский список карток каталогу
 *  і сортування цього списку. Pure: без Vue і без Firestore-імпортів —
 *  читання сабколекції варіантів приходить колбеком.
 *
 *  До REFACTOR_PLAN п.9 ця логіка існувала трьома копіями (catalog/index,
 *  catalog/pillowcases, HomeNewProducts) з РІЗНОЮ семантикою. Тут вони
 *  зведені, але місця, де різниця змістовна (джерело варіантів, критерій
 *  придатності, порядок усередині рангу), винесені в опції — а не злиті
 *  силою в один «правильний» варіант.
 * ==================================================================== */

import {
  isExclusiveVariant,
  isItemInStock,
  isVariantValid,
} from "~/composables/product/variantDisplay";

type AnyRecord = Record<string, unknown>;

export type GridProduct = AnyRecord;
export type GridVariant = AnyRecord;

/** Одна картка гріда: товар + конкретний варіант (або товар без варіантів). */
export interface GridItem {
  key: string;
  product: GridProduct;
  variant: GridVariant | null;
}

/* ---------- розгортання ---------- */

export interface ExpandOptions {
  /**
   * Спершу пробувати денормалізований `variantsLite`. Якщо він є і
   * непорожній — сабколекція НЕ читається (так було в каталозі).
   * `false` — читати виключно `fetchRemoteVariants`.
   *
   * Тут раніше стояло попередження, що `variantsLite` наповнює бек-філ
   * з адмінки і тому може бути сталим. Це НЕПРАВДА, і саме через неї
   * головна роками читала сабколекцію: lite пише тригер
   * `aggregateProductFromVariants` (functions/src/products.js) на кожен
   * запис варіанта, а ручний бек-філ — разова міграція для товарів,
   * створених до тригера. Перевірено на бойових даних перед п.9б: 12
   * товарів, 71 варіант, нуль розбіжностей lite проти сабколекції по
   * всіх полях, які читає картка.
   *
   * Опція лишається, але потреби в `false` більше немає: після 9б усі
   * гріди читають lite. Єдиний реальний привід — товар із понад 60
   * варіантами: стільки їх влазить у денормалізацію.
   */
  preferLite?: boolean;
  /** Читання `products/{id}/variants`. Без нього lite — єдине джерело. */
  fetchRemoteVariants?: (product: GridProduct) => Promise<unknown[]>;
  /** Критерій придатності варіанта. Дефолт — `isVariantValid`. */
  isValid?: (product: GridProduct, variant: GridVariant) => boolean;
  /** Стеля кількості карток на виході (застосовується після розгортання). */
  limit?: number;
}

function normalizeVariants(raw: unknown[]): GridVariant[] {
  return (raw || [])
    .filter((v): v is AnyRecord => !!v && typeof v === "object")
    .map((v) => ({ ...v, id: String(v.id || v.variantId || "") }))
    .filter((v) => !!v.id);
}

function liteVariantsOf(product: GridProduct): GridVariant[] | null {
  const lite = Array.isArray(product.variantsLite)
    ? (product.variantsLite as unknown[])
    : null;
  return lite && lite.length ? normalizeVariants(lite) : null;
}

/**
 * Ядро: товар + його варіанти → картки. Товар без жодного придатного
 * варіанта лишається однією карткою з `variant: null` (як було в усіх
 * трьох копіях).
 */
function itemsForProduct(
  product: GridProduct,
  variants: GridVariant[],
  isValid: NonNullable<ExpandOptions["isValid"]>,
): GridItem[] {
  const pid = String(product?.id || "");
  const valid = variants.filter((v) => isValid(product, v));
  if (!valid.length) return [{ key: pid, product, variant: null }];
  return valid.map((v) => ({
    key: `${pid}__${v.id}`,
    product,
    variant: v,
  }));
}

function applyLimit(items: GridItem[], limit?: number): GridItem[] {
  return typeof limit === "number" ? items.slice(0, limit) : items;
}

/**
 * Синхронне розгортання ТІЛЬКИ з денормалізованого `variantsLite`.
 *
 * Потрібне саме синхронним для SSR-сторінок: асинхронний варіант не
 * встигає до рендеру, і сторінка поїхала б у HTML без жодного товару —
 * для SEO-лендінгів це фатально.
 */
export function expandLiteToItems(
  products: GridProduct[],
  opts: Omit<ExpandOptions, "preferLite" | "fetchRemoteVariants"> = {},
): GridItem[] {
  const list = Array.isArray(products) ? products : [];
  const isValid = opts.isValid || isVariantValid;
  const flat = list.flatMap((p) =>
    itemsForProduct(p, liteVariantsOf(p) || [], isValid),
  );
  return applyLimit(flat, opts.limit);
}

/**
 * Розгортання з fallback у сабколекцію. Помилка читання варіантів одного
 * товару не валить решту.
 */
export async function expandProductsToItems(
  products: GridProduct[],
  opts: ExpandOptions = {},
): Promise<GridItem[]> {
  const list = Array.isArray(products) ? products : [];
  const isValid = opts.isValid || isVariantValid;

  const perProduct = await Promise.all(
    list.map(async (p): Promise<GridItem[]> => {
      try {
        const lite = opts.preferLite !== false ? liteVariantsOf(p) : null;
        const variants =
          lite ??
          (opts.fetchRemoteVariants
            ? normalizeVariants(await opts.fetchRemoteVariants(p))
            : []);
        return itemsForProduct(p, variants, isValid);
      } catch {
        return [{ key: String(p?.id || ""), product: p, variant: null }];
      }
    }),
  );

  return applyLimit(perProduct.flat(), opts.limit);
}

/* ---------- сортування ---------- */

/**
 * `createdAt` приходить у трьох формах залежно від шляху даних:
 * Firestore Timestamp (клієнтський SDK), ISO-рядок (`/api/catalog/*`,
 * див. `tsToIso`) і число. Стара `sortFlat` знала лише перші дві і тихо
 * давала 0 для рядка — на SSR-сторінках сортування «спочатку нові»
 * фактично не працювало. Тут покриті всі три.
 */
export function readCreatedAtMs(product: unknown): number {
  if (!product || typeof product !== "object") return 0;
  const raw = (product as AnyRecord).createdAt;
  if (raw == null) return 0;

  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const ms = Date.parse(raw);
    return Number.isNaN(ms) ? 0 : ms;
  }
  if (typeof raw === "object") {
    const o = raw as AnyRecord;
    if (typeof o.toMillis === "function") {
      return Number((o.toMillis as () => number)()) || 0;
    }
    if (typeof o.seconds === "number") return o.seconds * 1000;
  }
  return 0;
}

export type GridSortMode = "new" | "price-asc" | "price-desc" | "keep-order";

function itemPrice(it: GridItem): number {
  return Number(it.variant?.price ?? it.product?.price ?? 0);
}

/**
 * Ранг картки — головний ключ сортування, спільний для всіх гридів:
 * 0 звичайні в наявності → 1 ексклюзив у наявності → 2 без наявності.
 */
export function gridRank(it: GridItem): number {
  const inStock = isItemInStock(it.product, it.variant);
  if (!inStock) return 2;
  return isExclusiveVariant(it.variant) ? 1 : 0;
}

/**
 * Сортування карток. Ранг завжди головний; `mode` керує лише порядком
 * УСЕРЕДИНІ рангу. `keep-order` зберігає вхідний порядок (потрібно там,
 * де він змістовний — бажане, нещодавно переглянуті).
 */
export function sortGridItems(
  items: GridItem[],
  mode: GridSortMode = "new",
): GridItem[] {
  const rows = (items || []).map((it, i) => ({
    it,
    i,
    rank: gridRank(it),
    price: itemPrice(it),
    createdAt: readCreatedAtMs(it.product),
  }));

  const dir = mode === "price-desc" ? -1 : 1;

  rows.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;

    if (mode === "price-asc" || mode === "price-desc") {
      if (a.price !== b.price) return (a.price - b.price) * dir;
      return a.i - b.i;
    }

    if (mode === "new" && a.createdAt !== b.createdAt) {
      return b.createdAt - a.createdAt;
    }

    return a.i - b.i;
  });

  return rows.map((r) => r.it);
}
